import {Dispatch, ReactNode, SetStateAction, useState} from 'react';
import {BaseUser, Condition} from '@legit.health/ui';
import {TFunction} from 'i18next';
import {AnamnesisQuestion} from '@/shared/interfaces/AnamnesisQuestion';
import {EncounterId} from '@/shared/interfaces/Encounter';
import AnamnesisStep from '../../../../steps/AnamnesisStep';
import {ConditionInfo, CreateDiagnosticReportResponse} from '../../../../types';
import anamnesisSessionStorage from '../../../../utils/anamnesisSessionStorage';
import InstructionsStep from '../Instructions';
import ApasiGlobalForm from './ApasiGlobalForm';

export default function ApasiGlobal({
  isForClinicalTrials,
  anamnesisQuestions,
  visitIdentifier,
  setCurrentStep,
  onSuccess,
  patient,
  steps,
  currentStep,
  t,
  conditions,
  pathologyId,
  conditionInfo
}: {
  anamnesisQuestions: AnamnesisQuestion[];
  conditions: Condition[];
  conditionInfo: ConditionInfo | null;
  currentStep: number;
  extraPostData?: (conditionId: string | null) => Record<string, any>;
  isForClinicalTrials: boolean;
  onSuccess: (values: CreateDiagnosticReportResponse) => void;
  pathologyId: string | null;
  patient: BaseUser;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  steps: {label: string; icon: ReactNode}[];
  t: TFunction;
  visitIdentifier: string;
  withTemporaryImages: boolean;
}) {
  const condition = pathologyId
    ? conditions.find((c) => c.pathology.id === parseInt(pathologyId))
    : undefined;

  const stepNumberModifier = isForClinicalTrials ? 1 : 0;
  const [form, setForm] = useState(() => ({
    anamnesisQuestions:
      anamnesisSessionStorage.get() ??
      anamnesisQuestions.reduce(
        (acc, aq) => ({
          ...acc,
          [aq.id]: ''
        }),
        {}
      )
  }));

  function handleSuccess(encounterId: EncounterId) {
    onSuccess({
      encounterId: encounterId,
      type: 'encounter',
      conditionId: condition ? `${condition.id}` : null
    });
  }

  const stepComponents = {
    [1 - stepNumberModifier]: (
      <AnamnesisStep
        anamnesisQuestions={anamnesisQuestions}
        value={form.anamnesisQuestions}
        onBack={() => setCurrentStep((s) => s - 1)}
        onNext={function (value) {
          setForm((f) => ({...f, anamnesisQuestions: value}));
          setCurrentStep((s) => s + 1);
        }}
        t={t}
        activeStep={1}
        totalSteps={steps.length}
      />
    ),
    [2 - stepNumberModifier]: (
      <InstructionsStep
        onBack={() => setCurrentStep((s) => s - 1)}
        onNext={() => setCurrentStep((s) => s + 1)}
        t={t}
        activeStep={2 - stepNumberModifier}
        totalSteps={steps.length}
        condition={condition}
        conditionInfo={conditionInfo}
        pathologyId={pathologyId}
      />
    ),
    [3 - stepNumberModifier]: (
      <ApasiGlobalForm
        patientId={patient.id}
        setCurrentStep={setCurrentStep}
        steps={steps}
        t={t}
        onSuccess={handleSuccess}
        anamnesisAnswers={form.anamnesisQuestions}
        visitIdentifier={visitIdentifier}
      />
    )
  };

  return <div>{stepComponents[currentStep] ?? null}</div>;
}
